import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Select,
  Typography,
  FormControl,
  FormControlLabel,
  Stack,
  InputLabel,
  Checkbox,
  Grid2,
  Tooltip,
  Box,
  Divider,
  Autocomplete,
  FormHelperText,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Avatar,
  InputAdornment,
} from "@mui/material";
import { SchoolRounded } from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { DateTime } from "luxon";
import { useDispatch, useSelector } from "react-redux";
import API from "../../api/api";
import useGetLocations from "../../Hooks/useGetLocation";
import { setSuccessSnack } from "../../Reducers/snackSuccess";
import { setSnackError } from "../../Reducers/snackErrors";
import usePermissions from "../../Reducers/usePermissions";
import { ADMIN, ROOT, TUTOR } from "../../Static/static";
import { TimePicker } from "@mui/x-date-pickers";
import useGetDistricts from "../../Hooks/useGetDistricts";
import LocationSelect from "../Select/LocationSelect";
import SemesterSelect from "../Select/SemesterSelect";
import DistrictSelect from "../Select/DistrictSelect";


const RACE_OPTIONS = [
  "American Indian or Alaska Native",
  "Asian",
  "Black or African American",
  "Hispanic or Latino",
  "Native Hawaiian or Other Pacific Islander",
  "White",
  "Two or More Races",
  "Other",
  "Prefer not to say",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
      sx={{ textTransform: "uppercase", letterSpacing: "0.06em", display: "block", mb: 1 }}
    >
      {children}
    </Typography>
  );
}

// ─── Validation ───────────────────────────────────────────────────────────────
const validationSchema = Yup.object({
  id: Yup.number().nullable(),
  firstname: Yup.string().required("First name is required").max(255),
  middle_name: Yup.string().notRequired(),
  last_name: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email").notRequired(),
  grade_level: Yup.string(),
  active: Yup.boolean(),
  created_at: Yup.date(),
  timeframe: Yup.boolean().notRequired(),
  location_id: Yup.number().required("Location is required").typeError("Location is required"),
  organization_id: Yup.number(),
  period: Yup.number().max(12).notRequired(),
  semester_id: Yup.number().required("Semester is required"),
  created_by: Yup.string(),
  direct_partnership: Yup.boolean(),
  tutor_id: Yup.number().notRequired().nullable(),
  teacher_id: Yup.number().notRequired(),
  timeframe_start: Yup.string().notRequired(),
  duration_required: Yup.boolean(),
  timeframe_end: Yup.string().notRequired(),
  race: Yup.string().nullable().oneOf([null, ...RACE_OPTIONS], "Invalid selection"),
  gender: Yup.mixed().oneOf(["Male", "Female"]).notRequired(),
  district_id: Yup.number().notRequired().nullable(),
});

// ─── Main component ───────────────────────────────────────────────────────────
const AddStudent = ({ open, onClose, initialData, minimal = false, disable }) => {
  const role = useSelector((state) => state.role);
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [teachers, setTeachers] = useState(null);

  const { semesters, locations, loading: location_loading, fetchSemesterLocation } = useGetLocations(role);
  const { districts, fetchDistricts } = useGetDistricts();

  const isEdit = !!initialData?.id;

  const initialValues = {
    id: initialData?.id ?? null,
    firstname: initialData?.first_name ?? "",
    middle_name: initialData?.middle_name ?? "",
    last_name: initialData?.last_name ?? "",
    email: initialData?.email ?? "",
    grade_level: initialData?.grade_level ?? "",
    timeframe: initialData?.timeframe ?? false,
    timeframe_start: initialData?.timeframe_start ?? null,
    timeframe_end: initialData?.timeframe_end ?? null,
    active: initialData?.active ?? false,
    created_at: initialData?.created_at ?? DateTime.local().toUTC().toISO(),
    location_id: initialData?.location_id ?? null,
    organization_id: role.organization_id,
    period: initialData?.period ?? null,
    semester_id: initialData?.semester_id ?? null,
    duration_required: initialData?.duration_required ?? false,
    direct_partnership: initialData?.direct_partnership ?? false,
    created_by: role.role,
    teacher_id: initialData?.teacher_id ?? null,
    race: initialData?.race ?? null,
    gender: initialData?.gender ?? null,
    district_id: null,
    tutor_id: role.role === TUTOR ? role.id : null,
  };

  useEffect(() => {
    fetchDistricts();
  }, []);

  useEffect(() => {
    if (initialData?.location_id) {
      fetchSemesterLocation(initialData.location_id);
      getTeachers(initialData.location_id);
    }
    if (initialData?.timeframe_start) {
      setStartTime(DateTime.fromFormat(initialData.timeframe_start, "HH:mm"));
      setEndTime(DateTime.fromFormat(initialData.timeframe_end, "HH:mm"));
    }
    return () => {
      setStartTime(null);
      setEndTime(null);
    };
  }, [initialData]);

  const getTeachers = async (locationId) => {
    try {
      const response = await API.get("/get_teachers", { params: { location_id: locationId } });
      if (response.data.data) setTeachers(response.data.data);
    } catch (error) {
      if (error.isAuthError || error.isForbidden) return;
      dispatch(setSnackError({ title: "Error", body: error.message || "Unable to load teachers" }));
    }
  };

  const handleLocationChange = async (setFieldValue, e) => {
    const value = e.target.value;
    setTeachers(null);
    if (!value) return;
    await setFieldValue("location_id", value);
    await fetchSemesterLocation(value);
    await getTeachers(value);
  };

  const handleTeacherSelect = async (setFieldValue, e) => {
    const value = e.target.value;
    if (!value) return;
    await setFieldValue("teacher_id", value);
  };

  const handleStartTimeChange = async (setFieldValue, date) => {
    setStartTime(date);
    const fmt = DateTime.fromJSDate(new Date(date)).toFormat("HH:mm");
    await setFieldValue("timeframe_start", fmt);
  };

  const handleEndTimeChange = async (setFieldValue, date) => {
    setEndTime(date);
    const fmt = DateTime.fromJSDate(new Date(date)).toFormat("HH:mm");
    await setFieldValue("timeframe_end", fmt);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const endpoint = values.id != null ? "/update_student" : "/create_student";
      const request = await API.post(endpoint, values);
      if (request.data.status === "Updated") {
        dispatch(setSuccessSnack({ title: "Success", body: "Student updated" }));
      }
      if (request.data.status === "OK") {
        dispatch(setSuccessSnack({ title: "Success", body: `Student created (ID: ${request.data.id})` }));
      }
      onClose();
    } catch (error) {
      if (error.isAuthError || error.isForbidden) return;
      dispatch(
        setSnackError({
          title: error.isNetworkError ? "Connection Error" : "Error",
          body: error.message || "Unable to save student",
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Initials for edit mode header
  const initials =
    initialData?.first_name && initialData?.last_name
      ? `${initialData.first_name[0]}${initialData.last_name[0]}`.toUpperCase()
      : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={fullScreen}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: isEdit ? "secondary.main" : "primary.main",
              fontSize: "0.85rem",
              fontWeight: 700,
            }}
          >
            {isEdit ? (initials ?? <SchoolRounded fontSize="small" />) : <SchoolRounded fontSize="small" />}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {isEdit ? "Edit student" : "Add student"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isEdit
                ? `Editing ${initialData.first_name} ${initialData.last_name}`
                : "Fill in the student's details below"}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <Divider sx={{ mt: 2 }} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, values, handleChange, setFieldValue, isSubmitting, isValid }) => (
          <Form>
            <DialogContent sx={{ px: 3, py: 2, flex: 1, overflowY: "auto" }}>
              <Stack spacing={1.5}>

                {/* ── Personal info ── */}
                <Box>
                  <SectionLabel>Personal info</SectionLabel>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="First name"
                        name="firstname"
                        value={values.firstname ?? ""}
                        onChange={handleChange}
                        error={touched.firstname && Boolean(errors.firstname)}
                        helperText={touched.firstname && errors.firstname}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Last name"
                        name="last_name"
                        value={values.last_name ?? ""}
                        onChange={handleChange}
                        error={touched.last_name && Boolean(errors.last_name)}
                        helperText={touched.last_name && errors.last_name}
                      />
                    </Stack>

                    <Stack direction="row" spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Middle name (optional)"
                        name="middle_name"
                        value={values.middle_name ?? ""}
                        onChange={handleChange}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Email (optional)"
                        name="email"
                        type="email"
                        value={values.email ?? ""}
                        onChange={handleChange}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Stack>
                  </Stack>
                </Box>

                <Divider />

                {/* ── Demographics ── */}
                <Box>
                  <SectionLabel>Demographics (optional)</SectionLabel>
                  <Stack spacing={1.5}>
                    <Autocomplete
                      options={RACE_OPTIONS}
                      value={values.race}
                      onChange={(_, newValue) => setFieldValue("race", newValue)}
                      clearOnEscape
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Race"
                          error={touched.race && Boolean(errors.race)}
                          helperText={touched.race && errors.race}
                        />
                      )}
                    />

                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                        Sex
                      </Typography>
                      <ToggleButtonGroup
                        color="secondary"
                        exclusive
                        value={values.gender ?? null}
                        onChange={(_, newValue) => setFieldValue("gender", newValue)}
                        size="small"
                      >
                        <ToggleButton value="Male" sx={{ px: 3, textTransform: "none" }}>
                          Male
                        </ToggleButton>
                        <ToggleButton value="Female" sx={{ px: 3, textTransform: "none" }}>
                          Female
                        </ToggleButton>
                      </ToggleButtonGroup>
                      {touched.gender && errors.gender && (
                        <Typography variant="caption" color="error" display="block" mt={0.5}>
                          {errors.gender}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>

                {/* ── Full-form only fields ── */}
                {!minimal && (
                  <>
                    <Divider />

                    {/* ── Flags ── */}
                    <Box>
                      <SectionLabel>Settings</SectionLabel>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="active"
                              color="secondary"
                              size="small"
                              checked={values.active}
                              onChange={(e) => setFieldValue("active", e.target.checked)}
                            />
                          }
                          label={
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Typography variant="body2">Active</Typography>
                              <Chip
                                label={values.active ? "On" : "Off"}
                                size="small"
                                color={values.active ? "success" : "default"}
                                variant={values.active ? "filled" : "outlined"}
                                sx={{ height: 16, fontSize: "0.62rem", fontWeight: 700 }}
                              />
                            </Stack>
                          }
                        />

                        <Tooltip title="Assign a specific attendance window to this student">
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="timeframe"
                                color="secondary"
                                size="small"
                                checked={values.timeframe ?? false}
                                onChange={(e) => setFieldValue("timeframe", e.target.checked)}
                              />
                            }
                            label={<Typography variant="body2">Timeframe</Typography>}
                          />
                        </Tooltip>

                        <Tooltip title="Track how much time the student spends under the program">
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="duration_required"
                                color="secondary"
                                size="small"
                                checked={values.duration_required}
                                onChange={(e) => setFieldValue("duration_required", e.target.checked)}
                              />
                            }
                            label={<Typography variant="body2">Duration tracking</Typography>}
                          />
                        </Tooltip>

                        {role?.role === TUTOR && (
                          <Tooltip title="Only you can view and manage this student record">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  color="secondary"
                                  name="direct_partnership"
                                  size="small"
                                  checked={values.direct_partnership}
                                  onChange={(e) => setFieldValue("direct_partnership", e.target.checked)}
                                />
                              }
                              label={<Typography variant="body2">Private submission</Typography>}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>

                    {/* ── Timeframe pickers ── */}
                    {values.timeframe && (
                      <Box>
                        <SectionLabel>Attendance window</SectionLabel>
                        <Stack direction="row" spacing={1.5}>
                          <TimePicker
                            label="Start time"
                            value={startTime}
                            onChange={(v) => handleStartTimeChange(setFieldValue, v)}
                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                          />
                          <TimePicker
                            label="End time"
                            value={endTime}
                            onChange={(v) => handleEndTimeChange(setFieldValue, v)}
                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                          />
                        </Stack>
                      </Box>
                    )}

                    <Divider />

                    {/* ── Location ── */}
                    <Box>
                      <SectionLabel>Location & placement</SectionLabel>
                      <Stack spacing={1.5}>

                        {(role?.role === ROOT || role?.role === ADMIN) && (
                          <DistrictSelect  
                            locations={districts ?? []}
                            onChange={(val, e) => setFieldValue('district_id', val)}
                            label="District"
                            value={values.district_id ?? ''}
                            error={touched.district_id && Boolean(errors.district_id)}
                            helperText={touched.district_id && errors.district_id}
                          />
                        )}

                        <LocationSelect 
                          locations={locations ?? []}
                          onChange={(val, e) => handleLocationChange(setFieldValue, e)}
                          label="Locations"
                          value={values.location_id ?? ''}
                          error={touched.location_id && Boolean(errors.location_id)}
                          helperText={touched.location_id && errors.location_id}
                        />

                        {/* Teacher — shown once location is set */}
                        {values.location_id && (
                          <FormControl
                            fullWidth
                            size="small"
                            error={touched.teacher_id && Boolean(errors.teacher_id)}
                          >
                            <InputLabel>Teacher (optional)</InputLabel>
                            <Select
                              name="teacher_id"
                              label="Teacher (optional)"
                              value={values.teacher_id ?? ""}
                              onChange={(e) => handleTeacherSelect(setFieldValue, e)}
                            >
                              <MenuItem value="">None</MenuItem>
                              {teachers?.map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                  <Stack>
                                    <Typography variant="body2">{item.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Room {item.room}
                                    </Typography>
                                  </Stack>
                                </MenuItem>
                              ))}
                            </Select>
                            {touched.teacher_id && errors.teacher_id && (
                              <FormHelperText>{errors.teacher_id}</FormHelperText>
                            )}
                          </FormControl>
                        )}
                      </Stack>
                    </Box>

                    <Divider />

                    {/* ── Academic ── */}
                    <Box>
                      <SectionLabel>Academic</SectionLabel>
                      <Stack spacing={1.5}>
                        <Grid2 container spacing={1.5}>
                          <Grid2 size={6}>
                            <FormControl
                              fullWidth
                              size="small"
                              error={touched.grade_level && Boolean(errors.grade_level)}
                            >
                              <InputLabel>Grade level</InputLabel>
                              <Select
                                name="grade_level"
                                label="Grade level"
                                value={values.grade_level ?? ""}
                                onChange={handleChange}
                              >
                                {[...Array(13)].map((_, i) => (
                                  <MenuItem key={i} value={i}>
                                    {i === 0 ? "Kindergarten" : `Grade ${i}`}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid2>

                          <Grid2 size={6}>
                            <FormControl
                              fullWidth
                              size="small"
                              error={touched.period && Boolean(errors.period)}
                            >
                              <InputLabel>Period</InputLabel>
                              <Select
                                name="period"
                                label="Period"
                                value={values.period ?? ""}
                                onChange={handleChange}
                              >
                                <MenuItem value={0}>N/A</MenuItem>
                                <MenuItem value={-100}>In-person tutoring</MenuItem>
                                <MenuItem value={-99}>After-school tutoring</MenuItem>
                                <MenuItem value={-98}>Summer tutoring</MenuItem>
                                <MenuItem value={-97}>Winter tutoring</MenuItem>
                                {[...Array(12)].map((_, i) => (
                                  <MenuItem key={i} value={i + 1}>
                                    Period {i + 1}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid2>
                        </Grid2>
                        {console.log(values.semester_id)}
                        {console.log(semesters)}
                        
                        <SemesterSelect
                            label="Semester"
                            semesters={semesters ?? []}
                            value={values.semester_id ?? ''}
                            onChange={(val, e) => setFieldValue('semester_id', val)}
                            error={touched.semester_id && Boolean(errors.semester_id)}
                            helperText={touched.semester_id && errors.semester_id}    
                          />
                        
                      </Stack>
                    </Box>
                  </>
                )}
              </Stack>
            </DialogContent>

            {/* ── Footer ── */}
            <Divider />
            <DialogActions sx={{ px: 3, py: 1.75, gap: 1 }}>
              <Button
                onClick={onClose}
                variant="text"
                sx={{ textTransform: "none", color: "text.secondary" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={(!hasPermission("write", "students") && !isValid) || disable || isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ textTransform: "none", fontWeight: 700, minWidth: 110 }}
              >
                {isSubmitting ? "Saving…" : isEdit ? "Save changes" : "Add student"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddStudent;